// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from 'react'
import { VideoCamera, SignOut } from '@phosphor-icons/react'
import { useI18n } from './i18n'
import { useRoute } from './router'
import * as api from './api'
import { Home } from './screens/Home'
import { Meeting } from './screens/Meeting'
import { SignIn } from './screens/SignIn'
import { Verify } from './screens/Verify'
import { Recording } from './screens/Recording'

export function App() {
  const { t, lang, locales, setLang } = useI18n()
  const [route, navigate] = useRoute()
  const [account, setAccount] = useState<api.Account | null>(null)
  const [booted, setBooted] = useState(false)

  /**
   * Đang ở TRONG phòng họp — khi đó thanh tiêu đề được thu lại.
   *
   * Chiều dọc là tài nguyên khan hiếm nhất của một màn hình họp: lưới video co
   * theo chiều cao còn lại, nên mỗi dải ngang nằm mãi trên đầu là một dải cắt
   * thẳng vào khuôn mặt mọi người. Ba thứ trong thanh này — logo, bộ chọn ngôn
   * ngữ, nút Đăng nhập — không thứ nào là việc người ta làm giữa cuộc họp.
   *
   * Chỉ thu ở phase 'in'. Ở prejoin và phòng chờ thì thanh này vẫn cần: người
   * đứng trước cửa có thể phải đổi ngôn ngữ để đọc được câu đang hiện, hoặc
   * đăng nhập để được nhận vào bằng vai trò thành viên.
   */
  const [inRoom, setInRoom] = useState(false)

  // Có cookie refresh sẵn thì khôi phục phiên ngay khi mở trang. Thất bại là
  // trường hợp BÌNH THƯỜNG — phần lớn người vào phòng không có tài khoản.
  useEffect(() => {
    void api.refresh().then((ok) => {
      if (ok) setAccount(api.session.account)
      setBooted(true)
    })
  }, [])

  if (!booted) return null

  return (
    <div className="app">
      {!inRoom && (
        <header className="topbar">
          <button className="brand linkish" onClick={() => navigate('/')}>
            <VideoCamera size={20} weight="light" />
            <span>{t('ui.app.name')}</span>
          </button>

          <div className="spacer" />

          <select
            value={lang}
            aria-label={t('ui.app.language')}
            onChange={(e) => setLang(e.target.value)}
            style={{ width: 'auto' }}
          >
            {locales.map((l) => (
              <option key={l.tag} value={l.tag}>
                {l.native_name}
              </option>
            ))}
          </select>

          {account ? (
            <button
              className="sec sm"
              onClick={async () => {
                await api.signOut()
                setAccount(null)
                navigate('/')
              }}
            >
              <SignOut size={16} weight="light" />
              {t('ui.auth.sign_out')}
            </button>
          ) : (
            <button className="sec sm" onClick={() => navigate('/signin')}>
              {t('ui.auth.sign_in')}
            </button>
          )}
        </header>
      )}

      {route.name === 'home' && <Home onJoined={(code) => navigate('/j/' + code)} />}
      {route.name === 'join' && (
        <Meeting code={route.code} account={account} onInRoom={setInRoom} />
      )}
      {route.name === 'room' && (
        <Meeting externalRoom={route.room} account={account} onInRoom={setInRoom} />
      )}
      {route.name === 'signin' && (
        <SignIn
          onSignedIn={(a) => {
            setAccount(a)
            navigate('/')
          }}
        />
      )}
      {/* Trang tải bản ghi bằng link chia sẻ. Không đòi đăng nhập: người bấm
          vào có thể chưa từng mở Z-Meet. Xem screens/Recording.tsx. */}
      {route.name === 'recording' && <Recording id={route.id} />}
      {route.name === 'verify' && <Verify token={route.token} />}
      {route.name === 'invite' && <Verify token={route.token} invite />}
      {route.name === 'notfound' && (
        <main className="center">
          <div className="card">
            <h1>{t('ui.app.not_found')}</h1>
          </div>
        </main>
      )}
    </div>
  )
}
